import {Icon} from '@/app/components/Icon';
import {forwardRef, ReactNode, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';
import {Button} from '@/components/app/button';
import {ChoiceSelect} from '@/components/app/choice-select';
import {useDebouncedState, useTimeout} from '@/app/hooks/useTimers';
import {Textarea, TextareaProps} from '@/components/ui/textarea';
import {cn} from '@/lib/utils';
import {useNavigate} from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import isEqual from 'lodash/isEqual';
import {useDebounceFn} from '../../../app/hooks/useDebounceFn';
import {useGetAIQuestionsQuery, useLazyAskAIQuery, useLazyAskAIQuestionQuery} from '../../../app/services/biotwinApi';
import {copyToClipboard, getErrorMessage} from '../../../app/utils/miscUtils';
import {useSession} from '../../auth/hooks/useSession';
import {AIQuestion, AIResult} from '../../biotwin/types';
import styles from './AIChat.module.css';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {selectBiobookPage} from '../personSlice';
const defaultDictionary = {
  askYourQuestion: 'Ask your question...',
  error: 'Error',
  send: 'Send',
  thinking: 'Thinking...',
};
export interface AIChatProps {
  userId: number;
}
export const AIChat = ({userId}: AIChatProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useSession();
  const [expanded, expandedDebounced, setExpanded] = useDebouncedState(false, 100);
  const [prompt, setPrompt] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [askAI] = useLazyAskAIQuery();
  const [askAIQuestion] = useLazyAskAIQuestionQuery();
  const {data: aiQuestions = []} = useGetAIQuestionsQuery();
  const [history, setHistory] = useState<
    {
      message: string | AIResult;
      sender: 'user' | 'bio-assistant' | 'error';
      timestamp: number;
    }[]
  >([]);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const chatInput = useRef<ChatInput>(null);
  const [language, setLanguage] = useState('en');
  const [dictionary, setDictionary] = useState(defaultDictionary);
  const [sending, setSending] = useState(false);
  const [selectedType, setSelectedType] = useState(4);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [strictness, setStrictness] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (scrollContainer.current) {
      const lastMessage = scrollContainer.current.querySelector(`.${styles.message}:last-child`);
      if (lastMessage) {
        scrollContainer.current.scrollTo({
          top: scrollContainer.current.scrollHeight - lastMessage.clientHeight,
          behavior: 'smooth',
        });
      } else {
        scrollContainer.current.scrollTo({
          top: scrollContainer.current.scrollHeight,
          behavior: 'smooth',
        });
      }
      chatInput.current?.focus();
    }
  }, [history]);
  useLayoutEffect(() => {
    if (expanded && scrollContainer.current) {
      scrollContainer.current.scrollTo({
        top: scrollContainer.current.scrollHeight,
        behavior: 'instant',
      });
    }
  }, [expanded]);
  return (
    <div className={'fixed bottom-0 right-0 mb-6 mr-12 z-40'}>
      <Button
        className={cn(
          'rounded-full shadow-lg transition-all',
          // Purple while the chat is open, as with PrimeReact's `help` severity.
          expanded && 'rounded-tr-none bg-purple-600 hover:bg-purple-700',
        )}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        variant="assistant"
        type="button">
        {'BioAssistant'}
      </Button>
      {(expanded || expandedDebounced) && (
        <div
          className={cn(
            'absolute bottom-full right-0 bg-white shadow-lg mb-4 rounded-t-xl rounded-l-xl transition-all overflow-hidden',
          )}
          style={{
            bottom: '100%',
            maxHeight: !expanded ? 0 : expandedDebounced ? '80vh' : 0,
            maxWidth: !expanded ? 0 : expandedDebounced ? panelWidth : 0,
            transitionDuration: '0.2s',
            transitionTimingFunction: 'linear',
            width: panelWidth,
          }}>
          <div
            className={'flex flex-col'}
            style={{
              maxHeight: '80vh',
              width: panelWidth,
            }}>
            <header className={'p-4'}>
              <div className={'flex items-center mb-2'}>
                <h2 className={'text-lg font-bold my-0'}>AI Chat</h2>
                <div
                  className={'flex items-center ml-auto'}
                  style={{
                    height: 0,
                  }}>
                  <Button
                    className={cn('-mr-2', 'rounded-full')}
                    onClick={() => setExpanded(false)}
                    type={'button'}
                    variant="ghost"
                    startIcon={<Icon icon={'times'} />}
                    aria-label={'Close'}
                    size="icon"
                  />
                </div>
              </div>
              <p className={'text-sm text-gray-600 my-0'}>Ask a question about this Biotwin.</p>
            </header>
            {!settingsVisible && (
              <>
                <div className={'flex-1 overflow-auto px-4 pb-4'} ref={scrollContainer}>
                  <ChatMessage
                    className={cn({
                      'mb-2': history.length > 0,
                    })}
                    message={
                      <>
                        <p className={'mt-0'}>Hi there!</p>
                        <p>I’m BioAssistant, here to assist you with personalized insights and information.</p>
                        <p>Here are some questions you can ask to get started:</p>
                        <ul>
                          <li>
                            <a
                              className={'text-foreground underline'}
                              href={''}
                              onClick={(e) => {
                                e.preventDefault();
                                send('', 0);
                              }}>
                              Can you give me a brief summary of this Biotwin?
                            </a>
                          </li>
                          {aiQuestions.map((question) => (
                            <li key={question.id}>
                              <a
                                className={'text-foreground underline'}
                                href={''}
                                onClick={(e) => {
                                  e.preventDefault();
                                  sendQuestion(question);
                                }}>
                                {question.question}
                              </a>
                            </li>
                          ))}
                        </ul>
                        <p>If you have any specific questions or topics in mind, feel free to ask!</p>
                        <p>Let’s get started!</p>
                      </>
                    }
                    sender={'bio-assistant'}
                    timestamp={Date.now()}
                  />
                  {history.map((item, index, array) => (
                    <ChatMessage
                      className={cn({
                        'mb-2': index < array.length - 1,
                      })}
                      key={index}
                      message={typeof item.message === 'string' ? item.message : renderAIResult(item.message)}
                      sender={item.sender}
                      timestamp={item.timestamp}
                    />
                  ))}
                </div>
                <div className={'px-4'}>
                  <div className="flex items-center text-right mb-1 mr-1 text-muted-foreground text-xs">
                    <ChoiceSelect
                      className="p-0 text-muted-foreground ml-auto text-xs border-0 w-auto mr-2 h-5 flex items-center"
                      options={[
                        {
                          label: 'English',
                          value: 'en',
                        },
                        {
                          label: 'Turkish',
                          value: 'tr',
                        },
                      ]}
                      optionLabel={'label'}
                      optionValue={'value'}
                      onValueChange={async (e) => {
                        const newLanguage = e;
                        setLanguage(newLanguage);
                        if (newLanguage === 'en') {
                          setDictionary(defaultDictionary);
                          return;
                        }
                        const {result} = await askAI({
                          language: newLanguage,
                          prompt: JSON.stringify(defaultDictionary),
                          type: 1,
                          userId,
                        }).unwrap();
                        setDictionary(JSON.parse(result));
                      }}
                      value={language}
                    />{' '}
                    {prompt.length} / 500
                  </div>
                  <div className={cn(styles.inputContainer, 'flex flex-col border border-gray-300 rounded-lg')}>
                    <ChatInput
                      autoFocus
                      className={cn(
                        styles.input,
                        'w-full rounded-b-none rounded-t-lg border-0 mb-0 focus-visible:ring-0',
                      )}
                      disabled={sending}
                      maxLength={500}
                      onChange={(value) => setPrompt(value)}
                      onSend={(value) => send(value, selectedType)}
                      placeholder={dictionary.askYourQuestion}
                      ref={chatInput}
                      rows={3}
                      style={{
                        resize: 'none',
                      }}
                      value={prompt}
                    />
                    <Button
                      className={cn(
                        'rounded-t-none rounded-b-lg w-full',
                        prompt && 'bg-purple-600 text-white hover:bg-purple-700',
                      )}
                      disabled={!prompt}
                      isLoading={sending}
                      onClick={() => send(prompt, selectedType)}
                      variant="secondary"
                      type="button"
                      startIcon={<Icon icon={'send'} />}>
                      {sending ? dictionary.thinking : dictionary.send}
                    </Button>
                  </div>
                </div>
              </>
            )}
            {settingsVisible && (
              <div className={'p-4 flex flex-col gap-4'}>
                <div>
                  <label className={'block mb-2 ml-4'} htmlFor="ai-chat-setting-type">
                    Type
                  </label>
                  <ChoiceSelect
                    inputId="ai-chat-setting-type"
                    onValueChange={(e) => setSelectedType(e)}
                    options={[
                      {
                        label: 'Summary',
                        value: 0,
                      },
                      {
                        label: 'Translation',
                        value: 1,
                      },
                      {
                        label: 'Biobook',
                        value: 2,
                      },
                      {
                        label: 'Biobook New',
                        value: 3,
                      },
                      {
                        label: 'ChatGPT',
                        value: 4,
                      },
                    ]}
                    optionLabel={'label'}
                    optionValue={'value'}
                    value={selectedType}
                  />
                </div>
                <div>
                  <label className={'block mb-2 ml-4'} htmlFor="ai-chat-setting-strictness">
                    Strictness
                  </label>
                  <ChoiceSelect
                    className={'w-full'}
                    inputId="ai-chat-setting-strictness"
                    onValueChange={(e) => setStrictness(e)}
                    options={[
                      {
                        label: 'Relaxed (1)',
                        value: 1,
                      },
                      {
                        label: 'Moderate (2)',
                        value: 2,
                      },
                      {
                        label: 'Strict (3)',
                        value: 3,
                      },
                      {
                        label: 'Very Strict (4)',
                        value: 4,
                      },
                      {
                        label: 'Most Strict (5)',
                        value: 5,
                      },
                    ]}
                    optionLabel={'label'}
                    optionValue={'value'}
                    showClear
                    value={strictness}
                  />
                </div>
                <div>
                  <label className={'block mb-2 ml-4'} htmlFor="ai-chat-setting-system-prompt">
                    System prompt
                  </label>
                  <Textarea
                    className={'rounded-3xl w-full'}
                    id="ai-chat-setting-system-prompt"
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    placeholder={'System prompt (optional)'}
                    rows={4}
                    value={systemPrompt}
                  />
                </div>
              </div>
            )}
            <footer className={'p-4 pt-0'}>
              <div className={'flex items-center gap-1'}>
                <p className={'text-muted-foreground text-xs mb-0'}>
                  AI-generated contents may be inaccurate or misleading. Be sure to double-check sources.
                </p>
                {session?.isAdmin === 'true' && (
                  <Button
                    onClick={() => setSettingsVisible((prev) => !prev)}
                    type={'button'}
                    variant="link"
                    startIcon={<Icon icon={'cog'} />}
                    aria-label={'cog'}
                    size="icon"
                  />
                )}
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
  function renderAIResult({rephrasedQuestion, result}: AIResult) {
    if (!result) {
      return <p>No result available.</p>;
    }
    if (result.includes('[Biobook Phrase #')) {
      const citationIndexByPhraseId: {
        [key: number]: number;
      } = {};
      let citationIndex = 0;
      result
        .match(/\[Biobook Phrase #\d+]/g)
        ?.map((match) => Number(match.split('#')[1].slice(0, -1)))
        .forEach((phraseId) => {
          if (!citationIndexByPhraseId[phraseId]) {
            citationIndexByPhraseId[phraseId] = ++citationIndex;
          }
        });
      const phraseHrefPrefix: string = `/biotwins/${userId}/biobook#`;
      const replacedResult = result.replaceAll(/\[Biobook Phrase #\d+]/g, (match) => {
        const phraseId = Number(match.split('#')[1].slice(0, -1));
        return `[[${citationIndexByPhraseId[phraseId]}]](${phraseHrefPrefix}${phraseId})`;
      });
      return (
        <div>
          <Markdown
            components={{
              a: ({children, href, ...otherProps}) => {
                if (!href?.startsWith(phraseHrefPrefix)) {
                  return (
                    <a {...otherProps} href={href}>
                      {children}
                    </a>
                  );
                }
                const phraseId = Number(href?.slice(phraseHrefPrefix.length) || 0);
                return (
                  <Button
                    className={'inline p-0 min-w-0'}
                    onClick={() => {
                      dispatch(selectBiobookPage(phraseId));
                      navigate(`/biotwins/${userId}/biobook`);
                      setExpanded(false);
                    }}
                    hint={`Click to show related biobook page. ${session?.isAdmin === 'true' ? phraseId : ''}`}
                    type={'button'}
                    variant="link">{`[${citationIndexByPhraseId[phraseId]}]`}</Button>
                );
              },
            }}
            remarkPlugins={[remarkGfm]}>
            {replacedResult}
          </Markdown>
          {rephrasedQuestion && session?.isAdmin === 'true' && (
            <div>
              <strong className="text-muted-foreground">Rephrased Question:</strong>
              <p className="text-muted-foreground mt-1">{rephrasedQuestion}</p>
            </div>
          )}
        </div>
      );
    }
    return (
      <>
        <Markdown>{result}</Markdown>
        {rephrasedQuestion && session?.isAdmin === 'true' && (
          <div className="mt-2">
            <strong className="text-muted-foreground">Rephrased Question:</strong>
            <p className="text-muted-foreground mt-1">{rephrasedQuestion}</p>
          </div>
        )}
      </>
    );
  }
  async function send(prompt: string, type: number = 0) {
    setSending(true);
    addMessageToHistory(prompt || (type === 0 ? 'Can you give me a brief summary of this Biotwin?' : ''), 'user');
    setPrompt('');
    try {
      const result = await askAI({
        prompt,
        systemPrompt,
        strictness,
        type,
        userId,
      }).unwrap();
      addMessageToHistory(result, 'bio-assistant');
    } catch (e) {
      addMessageToHistory(getErrorMessage(e), 'error');
    } finally {
      setSending(false);
    }
  }
  async function sendQuestion(question: AIQuestion) {
    setSending(true);
    addMessageToHistory(question.question, 'user');
    setPrompt('');
    try {
      const result = await askAIQuestion({
        questionId: question.id,
        userId,
      }).unwrap();
      addMessageToHistory(result, 'bio-assistant');
    } catch (e) {
      addMessageToHistory(getErrorMessage(e), 'error');
    } finally {
      setSending(false);
    }
  }
  function addMessageToHistory(message: string | AIResult, sender: 'user' | 'bio-assistant' | 'error') {
    setHistory((prev) => [
      ...prev,
      {
        message,
        sender,
        timestamp: Date.now(),
      },
    ]);
  }
};

// ChatInput component

interface ChatInputProps extends Omit<TextareaProps, 'onChange' | 'onKeyDown' | 'value'> {
  onChange: (value: string) => void;
  onSend: (value: string) => void;
  value: string;
}
interface ChatInput {
  focus: () => void;
  getElement: () => HTMLTextAreaElement | null;
}
const ChatInput = forwardRef<ChatInput, ChatInputProps>(({onChange, onSend, value, ...otherProps}, ref) => {
  const element = useRef<HTMLTextAreaElement>(null);
  const history = useRef<
    {
      selectionStart: number;
      selectionEnd: number;
      value: string;
    }[]
  >([
    {
      selectionStart: 0,
      selectionEnd: 0,
      value: '',
    },
  ]);
  const historyIndex = useRef(0);
  const pushHistoryDebounced = useDebounceFn(pushHistory, {
    delay: 500,
  });
  useImperativeHandle(ref, () => ({
    focus: () => element.current?.focus(),
    getElement: () => element.current,
  }));
  return (
    <Textarea
      {...otherProps}
      onChange={(e) => changeValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (e.shiftKey) {
            const currentTarget = e.currentTarget;
            const {selectionStart, selectionEnd} = currentTarget;
            const newValue = value.slice(0, selectionStart) + '\n' + value.slice(selectionEnd);
            changeValue(newValue);
            setTimeout(() => {
              currentTarget.setSelectionRange(selectionStart + 1, selectionStart + 1);
            }, 0);
          } else {
            if (value) {
              onSend(value);
            }
          }
        }
        if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          if (e.shiftKey) {
            goHistory(1);
          } else {
            goHistory(-1);
          }
        }
      }}
      ref={element}
      value={value}
    />
  );
  function changeValue(newValue: string) {
    onChange(newValue);
    pushHistoryDebounced(newValue);
    setTimeout(() => {
      if (element.current) {
        const paddingBottom = 12; // Assuming a padding-bottom of 12px
        const paddingTop = 12; // Assuming a padding-top of 12px
        const lineHeight = 19; // Assuming a line height of 19px
        const currentLine = newValue.slice(0, element.current.selectionStart ?? 0).split('\n').length;
        const currentLineY = lineHeight * (currentLine - 1);
        const {clientHeight, scrollTop} = element.current;
        if (scrollTop > currentLineY) {
          element.current.scrollTo(0, currentLineY);
        } else if (scrollTop + clientHeight - paddingTop < currentLineY + lineHeight) {
          element.current.scrollTo(0, currentLineY + lineHeight - clientHeight + paddingBottom);
        }
      }
    });
  }
  function goHistory(direction: number) {
    if (history.current.length > 0) {
      const newIndex = historyIndex.current + direction;
      if (newIndex >= 0 && newIndex < history.current.length) {
        historyIndex.current = newIndex;
        const {selectionStart, selectionEnd, value} = history.current[newIndex];
        element.current?.setSelectionRange(selectionStart, selectionEnd);
        onChange(value);
      }
    }
  }
  function pushHistory(newValue: string) {
    const newHistoryItem = {
      selectionStart: element.current?.selectionStart || 0,
      selectionEnd: element.current?.selectionEnd || 0,
      value: newValue,
    };
    if (historyIndex.current > 0 && isEqual(history.current[historyIndex.current], newHistoryItem)) {
      return;
    }
    history.current = history.current.slice(0, historyIndex.current + 1).concat(newHistoryItem);
    historyIndex.current = history.current.length - 1;
  }
});

// ChatMessage component

interface ChatMessageProps {
  className?: string;
  message: ReactNode;
  sender: 'user' | 'bio-assistant' | 'error';
  timestamp: number;
}
// The panel is anchored to the button 3rem from the right edge; keep it inside narrow screens.
const panelWidth = 'min(30rem, calc(100vw - 4rem))';
const ChatMessage = ({className, message, sender, timestamp}: ChatMessageProps) => {
  const messageContainer = useRef<HTMLDivElement>(null);
  return (
    <div
      className={cn(styles.message, 'p-4 rounded-lg', className, {
        'bg-blue-100': sender === 'user',
        'bg-green-100': sender === 'bio-assistant',
        'bg-red-100': sender === 'error',
      })}>
      <div className={'flex items-center mb-4'}>
        <strong>{sender === 'user' ? 'You' : sender === 'error' ? 'Error' : 'BioAssistant'}:</strong>
        <div
          className={cn(styles.copyToClipboardContainer, 'flex items-center ml-auto -mr-2')}
          style={{
            height: 0,
          }}>
          <CopyToClipboardButton text={() => messageContainer.current?.innerText ?? ''} />
        </div>
      </div>
      <div ref={messageContainer}>
        {typeof message === 'string' && <Markdown>{message}</Markdown>}
        {typeof message !== 'string' && message}
      </div>
    </div>
  );
};
interface CopyToClipboardButtonProps {
  className?: string;
  text: string | (() => string);
}
const CopyToClipboardButton = ({className, text}: CopyToClipboardButtonProps) => {
  const [copied, setCopied] = useState(false);
  useTimeout(() => setCopied(false), 5000, copied);
  return (
    <Button
      className={cn(className, 'rounded-full')}
      onClick={() => {
        const copyText = typeof text === 'function' ? text() : text;
        copyToClipboard(copyText);
        setCopied(true);
      }}
      hint={copied ? 'Copied' : 'Copy'}
      variant="ghost"
      type="button"
      startIcon={<Icon icon={copied ? 'check' : 'copy'} />}
      aria-label={'More actions'}
      size="icon"
    />
  );
};
